import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { trackClick } from '../utils/analytics';
import {
  MinimalGlobalStyle,
  Container,
  Inner,
  BackButton,
  Header,
  PageTitle,
  Bio,
  HeaderLinks,
  HeaderLink,
  Section,
  SectionTitle,
  Card,
  CardTop,
  CardTitle,
  CardDescription,
} from '../components/minimal';

const STOREFRONT_TOKEN = 'ptkn_0b42c760-0f26-4b7a-a140-efb11cd2d1bd';
const SHOP_DOMAIN = 'a5omic-shop.fourthwall.com';
const API_BASE = 'https://storefront-api.fourthwall.com/v1';

type Money = {
  value: number;
  currency: string;
};

type ProductImage = {
  url?: string;
  transformedUrl?: string;
};

type ProductVariant = {
  id: string;
  name: string;
  unitPrice?: Money;
  attributes?: {
    description?: string;
    color?: { name?: string; swatch?: string };
    size?: { name?: string };
  };
  stock?: {
    type?: string;
    inStock?: number | boolean;
  };
};

type Product = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  images?: ProductImage[];
  variants?: ProductVariant[];
};

const FALLBACK_PRODUCTS: Product[] = [
  {
    id: '42d2eefa-bdd6-4e07-b1e7-e6ca1af8e777',
    name: 'Brick 001',
    slug: 'brick-001',
    description: 'Brick 001 Shirt.',
    images: [{ url: '/images/brick-001-mascot.png' }],
    variants: [
      { id: '2e462042-3d98-4237-8cec-f0452c1fa2c3', name: 'Brick 001 - White, S', unitPrice: { value: 25, currency: 'USD' }, attributes: { description: 'White, S', color: { name: 'White', swatch: '#ffffff' }, size: { name: 'S' } } },
      { id: 'fe31de7b-4fb7-4f24-aa3a-d341d5cce166', name: 'Brick 001 - White, M', unitPrice: { value: 25, currency: 'USD' }, attributes: { description: 'White, M', color: { name: 'White', swatch: '#ffffff' }, size: { name: 'M' } } },
      { id: '3fcf7e75-1155-49cf-b0c9-8c5054c15633', name: 'Brick 001 - White, L', unitPrice: { value: 25, currency: 'USD' }, attributes: { description: 'White, L', color: { name: 'White', swatch: '#ffffff' }, size: { name: 'L' } } },
      { id: 'aaedb30d-12e7-4ca6-999e-1f2eaa2eb517', name: 'Brick 001 - White, XL', unitPrice: { value: 25, currency: 'USD' }, attributes: { description: 'White, XL', color: { name: 'White', swatch: '#ffffff' }, size: { name: 'XL' } } },
      { id: 'c81e94d4-7a52-4fe3-9bf9-1f9a18d021e6', name: 'Brick 001 - White, 2XL', unitPrice: { value: 27, currency: 'USD' }, attributes: { description: 'White, 2XL', color: { name: 'White', swatch: '#ffffff' }, size: { name: '2XL' } } },
      { id: 'e60bcb64-b7c6-4466-a252-b0a576e17ec4', name: 'Brick 001 - White, 3XL', unitPrice: { value: 29, currency: 'USD' }, attributes: { description: 'White, 3XL', color: { name: 'White', swatch: '#ffffff' }, size: { name: '3XL' } } },
      { id: '6ac576b8-e0d3-4f67-9593-e8a50166d2cf', name: 'Brick 001 - White, 4XL', unitPrice: { value: 31, currency: 'USD' }, attributes: { description: 'White, 4XL', color: { name: 'White', swatch: '#ffffff' }, size: { name: '4XL' } } },
    ],
  },
  {
    id: '0669101b-4bbc-43e4-9f65-7307a0603e91',
    name: 'Brick 002',
    slug: 'brick-002',
    description: 'Brick 002 Hat.',
    images: [{ url: '/images/brick-001-mascot.png' }],
    variants: [
      { id: '53994b1f-4c24-489a-bf1b-9dcc9d220486', name: 'Brick 002 - White, One size', unitPrice: { value: 20, currency: 'USD' }, attributes: { description: 'White, One size', color: { name: 'White', swatch: '#ffffff' }, size: { name: 'One size' } } },
    ],
  },
  {
    id: '1071df42-fd45-47bf-8311-36b694581b42',
    name: 'Brick 003',
    slug: 'brick-003',
    description: 'Brick 003 Stickers.',
    images: [{ url: '/images/brick-001-mascot.png' }],
    variants: [
      { id: 'b17c0feb-f397-49ea-890d-33c901b2fab5', name: 'Brick 003 - White, 3" x 3"', unitPrice: { value: 7, currency: 'USD' }, attributes: { description: 'White, 3" x 3"', color: { name: 'White', swatch: '#ffffff' }, size: { name: '3" x 3"' } } },
      { id: 'a298d0de-cc33-4ffc-9094-14fbff60489a', name: 'Brick 003 - White, 4" x 4"', unitPrice: { value: 7.2, currency: 'USD' }, attributes: { description: 'White, 4" x 4"', color: { name: 'White', swatch: '#ffffff' }, size: { name: '4" x 4"' } } },
      { id: 'fb20e7fe-c94b-461f-b10f-7b5fe7f03f01', name: 'Brick 003 - White, 5.5" x 5.5"', unitPrice: { value: 7.4, currency: 'USD' }, attributes: { description: 'White, 5.5" x 5.5"', color: { name: 'White', swatch: '#ffffff' }, size: { name: '5.5" x 5.5"' } } },
    ],
  },
  {
    id: '173a8101-aa5f-4b33-b39d-6a0c3aa356e7',
    name: 'Brick 004',
    slug: 'brick-004',
    description: 'Brick 004 Bucket Hat.',
    images: [{ url: '/images/brick-001-mascot.png' }],
    variants: [
      { id: '2f6309df-a906-45dc-8d68-87671f183579', name: 'Brick 004 - White, One size', unitPrice: { value: 22, currency: 'USD' }, attributes: { description: 'White, One size', color: { name: 'White', swatch: '#fefefe' }, size: { name: 'One size' } } },
    ],
  },
];

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px;
  gap: 2rem;
  align-items: center;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MascotPanel = styled.figure`
  margin: 0;
  padding: 1rem;
  border: 1px solid rgba(249, 115, 22, 0.28);
  border-radius: 10px;
  background:
    radial-gradient(circle at 50% 20%, rgba(249, 115, 22, 0.18), transparent 60%),
    rgba(255, 255, 255, 0.03);
  box-shadow: 0 0 36px rgba(249, 115, 22, 0.08);

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  @media (max-width: 640px) {
    max-width: 180px;
  }
`;

const StatusNote = styled.p`
  margin: 0.75rem 0 0;
  color: rgba(255, 255, 255, 0.52);
  font-size: 0.9rem;
  line-height: 1.6;
`;

const ShopGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 120px) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ProductImagePanel = styled.figure`
  margin: 0;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    height: auto;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const VariantGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const VariantButton = styled.button<{ $active?: boolean }>`
  border: 1px solid ${p => (p.$active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.14)')};
  border-radius: 8px;
  background: ${p => (p.$active ? 'var(--accent-18)' : 'rgba(255, 255, 255, 0.03)')};
  color: ${p => (p.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.68)')};
  cursor: pointer;
  font: inherit;
  padding: 0.5rem 0.7rem;
`;

const BuyButton = styled.button`
  border: 1px solid var(--accent);
  border-radius: 8px;
  background: var(--accent);
  color: #07080b;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  padding: 0.7rem 0.9rem;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ProductMeta = styled.p`
  color: rgba(255, 255, 255, 0.56);
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0.5rem 0 0;
`;

function formatPrice(money?: Money): string {
  if (!money) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: money.currency || 'USD',
  }).format(money.value);
}

function variantLabel(variant: ProductVariant): string {
  return variant.attributes?.size?.name
    || variant.attributes?.description
    || variant.name
    || 'Variant';
}

function plainText(value?: string): string {
  if (!value) return '';
  return value.replace(/<[^>]*>/g, '').trim();
}

function brickNumber(product: Product): number {
  const match = product.name.match(/brick\s*(\d+)/i) || product.slug.match(/brick-(\d+)/i);
  return match ? Number(match[1]) : 999;
}

function sortProducts(products: Product[]): Product[] {
  return [...products].sort((a, b) => brickNumber(a) - brickNumber(b) || a.name.localeCompare(b.name));
}

function selectedMap(products: Product[]): Record<string, string> {
  const next: Record<string, string> = {};
  for (const product of products) {
    const variantId = product.variants?.[0]?.id;
    if (variantId) next[product.id] = variantId;
  }
  return next;
}

function checkoutUrl(variantId: string): string {
  const params = new URLSearchParams({
    products: `${variantId}:1`,
    currency: 'USD',
    cart_origin: 'a5omic_merch',
  });
  return `https://${SHOP_DOMAIN}/cart/checkout?${params.toString()}`;
}

const MerchPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedVariantIds, setSelectedVariantIds] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams({
      storefront_token: STOREFRONT_TOKEN,
      currency: 'USD',
      size: '50',
      v: 'brick-four-item-shop',
      t: Date.now().toString(),
    });

    fetch(`${API_BASE}/collections/all/products?${params.toString()}`, { cache: 'no-store' })
      .then(res => {
        if (!res.ok) throw new Error(`Fourthwall returned ${res.status}`);
        return res.json();
      })
      .then(data => {
        const nextProducts = sortProducts(Array.isArray(data.results) ? data.results : []);
        setProducts(nextProducts);
        setSelectedVariantIds(selectedMap(nextProducts));
        setError('');
      })
      .catch(() => {
        setError('');
        const fallbackProducts = sortProducts(FALLBACK_PRODUCTS);
        setProducts(fallbackProducts);
        setSelectedVariantIds(selectedMap(fallbackProducts));
      })
      .finally(() => setLoading(false));
  }, []);

  const sortedProducts = useMemo(() => sortProducts(products), [products]);

  const handleBuy = (product: Product, variant?: ProductVariant) => {
    if (!variant?.id) return;
    const destination = checkoutUrl(variant.id);
    trackClick('brick_store_checkout', { product: product.slug, variant: variant.id });
    window.location.href = destination;
  };

  return (
    <>
      <MinimalGlobalStyle $accent="#f97316" />
      <Container>
        <BackButton />
        <Inner>
          <Header>
            <HeroGrid>
              <div>
                <PageTitle>BRICK 001</PageTitle>
                <Bio>
                  Four live Brick pieces. Pick an item here, then
                  checkout runs through Fourthwall so the order can be made and shipped.
                </Bio>
                <HeaderLinks>
                  <HeaderLink href={`https://${SHOP_DOMAIN}`} target="_blank" rel="noopener noreferrer">
                    Fourthwall shop
                  </HeaderLink>
                  <HeaderLink href="https://tomryan.dev/merch" target="_blank" rel="noopener noreferrer">
                    TomRyan.dev
                  </HeaderLink>
                </HeaderLinks>
                <StatusNote>
                  Connected to the live A5omic Fourthwall storefront.
                </StatusNote>
              </div>
              <MascotPanel>
                <img src="/images/brick-001-mascot.png" alt="BRICK 001 mascot artwork" />
              </MascotPanel>
            </HeroGrid>
          </Header>

          <Section>
            <SectionTitle>Buy</SectionTitle>
            {loading && (
              <Card>
                <CardDescription>Loading live Fourthwall products...</CardDescription>
              </Card>
            )}
            {!loading && error && (
              <Card>
                <CardTitle>Store unavailable</CardTitle>
                <CardDescription>{error}</CardDescription>
              </Card>
            )}
            {!loading && !error && sortedProducts.length === 0 && (
              <Card>
                <CardTop>
                  <CardTitle>No published products yet</CardTitle>
                </CardTop>
                <CardDescription>
                  Fourthwall is connected, but the A5omic shop is still in coming-soon mode
                  and no product is published. Publish Brick in Fourthwall and this page
                  will become buyable automatically.
                </CardDescription>
              </Card>
            )}
            {!loading && !error && sortedProducts.length > 0 && (
              <ShopGrid>
                {sortedProducts.map(product => {
                  const variants = product.variants || [];
                  const selectedVariantId = selectedVariantIds[product.id] || variants[0]?.id || '';
                  const selectedVariant = variants.find(variant => variant.id === selectedVariantId) || variants[0];
                  const imageUrl = product.images?.[0]?.transformedUrl
                    || product.images?.[0]?.url
                    || '/images/brick-001-mascot.png';

                  return (
                    <Card key={product.id}>
                      <ProductGrid>
                        <ProductImagePanel>
                          <img src={imageUrl} alt={product.name} />
                        </ProductImagePanel>
                        <div>
                          <CardTop>
                            <CardTitle>{product.name}</CardTitle>
                          </CardTop>
                          <CardDescription>
                            {plainText(product.description) || 'Brick mascot product.'}
                          </CardDescription>
                          <ProductMeta>
                            {selectedVariant ? formatPrice(selectedVariant.unitPrice) : 'Select an option'}
                          </ProductMeta>
                          <VariantGrid>
                            {variants.map(variant => (
                              <VariantButton
                                key={variant.id}
                                $active={variant.id === selectedVariant?.id}
                                onClick={() => setSelectedVariantIds(prev => ({
                                  ...prev,
                                  [product.id]: variant.id,
                                }))}
                              >
                                {variantLabel(variant)}
                              </VariantButton>
                            ))}
                          </VariantGrid>
                          <BuyButton
                            disabled={!selectedVariant?.id}
                            onClick={() => handleBuy(product, selectedVariant)}
                          >
                            Buy now
                          </BuyButton>
                        </div>
                      </ProductGrid>
                    </Card>
                  );
                })}
              </ShopGrid>
            )}
          </Section>

          <Section>
            <SectionTitle>Fulfillment</SectionTitle>
            <Card>
              <CardTop>
                <CardTitle>Fourthwall checkout</CardTitle>
              </CardTop>
              <CardDescription>
                Payment, shipping address, order creation, production, and fulfillment are handled
                by Fourthwall after checkout. This site owns the storefront surface only.
              </CardDescription>
            </Card>
          </Section>
        </Inner>
      </Container>
    </>
  );
};

export default MerchPage;
